import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import { DatePicker, Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { components } from "react-select";
import { useGetDocumentsQuery } from "services/documents-api";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

import type { OptionProps, SingleValueProps } from "react-select";
import type { DocumentType } from "components/Documents/DocumentTypes";
import type {
  MSADataType,
  CostProjectionDataType,
  DocumentManagementDataType,
} from "types/DataTypes";

export type MedicalRecordIndexRow = {
  id: number;
  date: string;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  name: string;
  page_number: number;
  type: string;
  providers: string;
  organization: number;
  file: number;
  document: number | null;
  status: string | null;
};

export type MedicalRecordIndexPayload = Omit<
  MedicalRecordIndexRow,
  | "id"
  | "created"
  | "modified"
  | "deleted_at"
  | "version"
  | "organization"
  | "file"
>;

type MedicalRecordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: MedicalRecordIndexPayload) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  initialData?: MedicalRecordIndexRow;
  title: string;
  service: MSADataType | CostProjectionDataType | DocumentManagementDataType;
};

type DocumentOption = {
  label: string;
  value: number;
};

type StatusOptionType = {
  label: string;
  value:
    | "PAID"
    | "UNPAID"
    | "UNKNOWN"
    | "PRESENT_AND_PAID"
    | "PRESENT_NOT_PAID"
    | "NEEDS_REVIEW";
  color: "green" | "purple" | "white";
};

const DocumentOption = (props: OptionProps<DocumentOption>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const DocumentSingleValue = (props: SingleValueProps<DocumentOption>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const StatusOption = (props: OptionProps<StatusOptionType>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <div
        className={props.data.color}
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor:
            props.data.color === "white" ? "#ffffff" : props.data.color,
          border: props.data.color === "white" ? "1px solid #ccc" : "none",
          flexShrink: 0,
          marginRight: "8px",
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const StatusSingleValue = (props: SingleValueProps<StatusOptionType>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      <div
        className={props.data.color}
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor:
            props.data.color === "white" ? "#ffffff" : props.data.color,
          border: props.data.color === "white" ? "1px solid #ccc" : "none",
          flexShrink: 0,
          marginRight: "8px",
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const statusOptions = [
  { label: "Paid", value: "PAID", color: "green" },
  { label: "Unpaid", value: "UNPAID", color: "purple" },
  { label: "Unknown", value: "UNKNOWN", color: "white" },
] as const;

const FormErrors = ({
  errors,
}: {
  errors: FieldErrors<MedicalRecordIndexPayload>;
}) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const MedicalRecordModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialData,
  title,
  service,
}: MedicalRecordModalProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MedicalRecordIndexPayload>({
    defaultValues: {
      name: "",
      page_number: 1,
      date: "",
      type: "",
      providers: "",
      document: null,
      status: "PAID",
    },
    mode: "onBlur",
    shouldFocusError: false,
  });

  const { data: documentsData } = useGetDocumentsQuery(
    {
      fileId: service.file.id.toString(),
      limit: "1000",
      offset: "0",
      sortBy: "original_filename",
    },
    {
      skip: !service.file.id || !isOpen,
    },
  );

  const documents = documentsData?.results || [];

  // convert documents to options
  const documentOptions: DocumentOption[] = documents.map(
    (doc: DocumentType) => ({
      label: doc.original_filename,
      value: doc.id,
    }),
  );

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      reset({
        name: initialData.name,
        page_number: initialData.page_number,
        date: initialData.date,
        type: initialData.type,
        providers: initialData.providers,
        document: initialData.document,
        status: initialData.status,
      });
    } else {
      reset({
        name: "",
        page_number: 1,
        date: "",
        type: "",
        providers: "",
        document: null,
        status: "PAID",
      });
    }
  }, [isOpen, initialData, reset]);

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  const handleDelete = async () => {
    try {
      if (onDelete && initialData) {
        await onDelete(initialData.id.toString());
        setShowDeleteConfirm(false);
        onClose();
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  return (
    <>
      <KaboodleModal
        isOpen={isOpen}
        onClose={onClose}
        footer={
          <div className="flex jc-c">
            <Button onClick={handleFormSubmit}>Save</Button>
            <Button onClick={onClose}>Cancel</Button>
          </div>
        }
      >
        <Card
          heading={title}
          headerButton={
            initialData && (
              <ButtonIcon
                kind="neutral"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                className="delete"
              >
                delete
              </ButtonIcon>
            )
          }
        >
          <FormErrors errors={errors} />
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="Name"
                errors={errors}
              />
            )}
          />
          <Controller
            name="page_number"
            control={control}
            rules={{
              required: "Page number is required",
              min: { value: 1, message: "Page number must be at least 1" },
            }}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="Page Number"
                type="number"
                errors={errors}
              />
            )}
          />
          <Controller
            name="date"
            control={control}
            rules={{
              required: "Date is required",
              validate: (value) => {
                if (!value) return "Date is required";
                const date = STRING_TO_DATE(value);
                if (!date) return "Invalid date format";
                if (date > new Date()) return "Date cannot be in the future";
                return true;
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <DatePicker
                {...field}
                initialDate={STRING_TO_DATE(value) || undefined}
                onDateChange={(date) => {
                  onChange(DATE_TO_STRING(date));
                }}
                label="Date"
                errors={errors}
              />
            )}
          />
          <Controller
            name="type"
            control={control}
            rules={{ required: "Type is required" }}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="Record Type"
                errors={errors}
              />
            )}
          />
          <Controller
            name="document"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <Select
                {...field}
                label="Associated Document"
                options={documentOptions}
                value={documentOptions.find((option) => option.value === value)}
                errors={errors}
                components={{
                  Option: DocumentOption,
                  SingleValue: DocumentSingleValue,
                }}
                onChange={(option) => {
                  onChange(option ? (option as DocumentOption).value : null);
                }}
                isClearable
                isSearchable={false}
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field: { onChange, value, ...field } }) => (
              <Select
                {...field}
                label="Status"
                options={statusOptions}
                value={statusOptions.find((option) => option.value === value)}
                errors={errors}
                components={{
                  Option: StatusOption,
                  SingleValue: StatusSingleValue,
                }}
                onChange={(option) => {
                  onChange(option ? (option as StatusOptionType).value : null);
                }}
                isClearable={false}
                isSearchable={false}
              />
            )}
          />
          <Controller
            name="providers"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="Provider"
                errors={errors}
              />
            )}
          />
        </Card>
      </KaboodleModal>

      <KaboodleModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        footer={
          <div className="flex jc-c">
            <Button className="delete" onClick={handleDelete}>
              Confirm Delete
            </Button>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          </div>
        }
        children={
          <Card heading="Delete Medical Record">
            <p>Are you sure you want to delete this medical record?</p>
          </Card>
        }
      />
    </>
  );
};

export default MedicalRecordModal;
